import { useApolloClient } from '@apollo/client';
import { Button, Card, Form, message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useLoadingButton from '../../../components/button/useLoadingButton';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import { type ModuleClass, type CompanySpecs } from './getAvailableModules';

export type ModuleClassConfigurationProps = {
    company: CompanySpecs;
    moduleClass: ModuleClass;
    goBack: () => void;
};

const ModuleClassConfiguration = ({ company, moduleClass, goBack }: ModuleClassConfigurationProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const companyId = company.id;
    const companyTimeZone = company.timeZone;

    const { create, getValidator } = moduleClass;
    const { setLoading, LoadingButton } = useLoadingButton();

    const validator = useMemo(() => getValidator(), [getValidator]);
    const validatorContext = useMemo(() => ({ company }), [company]);
    const validate = useValidator(validator, validatorContext);

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const onSubmit = useHandleError(
        async values => {
            setLoading(true);
            message.loading({
                content: t('moduleDetails:addModule.configuration.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            // submit endpoint to API
            const module = await create(apolloClient, companyId, values, companyTimeZone).finally(() => {
                message.destroy('primary');
                setLoading(false);
            });

            // show success message
            message.success({
                content: t('moduleDetails:addModule.configuration.messages.success'),
                key: 'primary',
            });
            // then go back to router
            navigate(`/admin/system/modules/${module.id}`);
        },
        [setLoading, t, create, apolloClient, companyId, companyTimeZone, navigate]
    );

    const { FormComponent } = moduleClass;

    return (
        <Card>
            <Formik initialValues={moduleClass.initialValues ?? {}} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit, values }) => (
                    <Form layout="vertical" onSubmitCapture={handleSubmit}>
                        <FormComponent company={company} />
                        <div style={{ textAlign: 'right' }}>
                            <Button onClick={goBack} type="default">
                                {t('moduleDetails:addModule.configuration.cancelButton')}
                            </Button>
                            <LoadingButton disabled={!values.displayName} htmlType="submit" type="primary">
                                {t('moduleDetails:addModule.configuration.submitButton')}
                            </LoadingButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

export default ModuleClassConfiguration;
